'use client';

import { forwardRef } from 'react';

import { AnalyticsSkuViewed } from '@ria-client/segment/Analytics/AnalyticsSkuViewed';
import type { FinancialAdvisorsSummaryTableEntityType } from '@ria-client/supergraph/structuredContent/hydration/FinancialAdvisorsSummaryTable/types';
import { NumericLayout } from '@ria-client/tw-shared-ui';
import type { DriverMappingConfig } from '../lib/financial-advisors/types';
import { OfferCta } from './offer-cta';
import { TableCellCompany } from './table-cell-company';
import { TableDrivers } from './table-drivers';
import { TableRowLearnMore } from './table-row-learn-more';

export interface SummaryTableBodyProps {
  marketplaceEntities: FinancialAdvisorsSummaryTableEntityType[];
  drivers: DriverMappingConfig[];
  monetizingCtaLabel?: string;
  displayAdLabel?: boolean;
}

export function SummaryTableBody({
  marketplaceEntities,
  drivers,
  monetizingCtaLabel,
  displayAdLabel,
}: SummaryTableBodyProps) {
  return (
    <tbody className="border-b border-border">
      {marketplaceEntities.map((marketplaceEntity, index) => (
        <AnalyticsSkuViewed
          impressionPosition={index}
          impressionSection="financial_advisors_summary_table"
          institutionId={marketplaceEntity.institution.id}
          institutionName={marketplaceEntity.institution.name}
          key={marketplaceEntity.id}
          level="offer"
          monetizable={marketplaceEntity.monetizable}
          name={marketplaceEntity.name}
          offerId={marketplaceEntity.id}
          offerName={marketplaceEntity.name}
          productId={marketplaceEntity.product.id}
          productName={marketplaceEntity.product.name}
          productType={marketplaceEntity.productType}
          source="marketplace"
        >
          <TableRowPair
            displayAdLabel={displayAdLabel}
            drivers={drivers}
            index={index}
            marketplaceEntity={marketplaceEntity}
            monetizingCtaLabel={monetizingCtaLabel}
          />
        </AnalyticsSkuViewed>
      ))}
    </tbody>
  );
}

interface TableRowPairProps {
  marketplaceEntity: FinancialAdvisorsSummaryTableEntityType;
  drivers: DriverMappingConfig[];
  index: number;
  monetizingCtaLabel?: string;
  displayAdLabel?: boolean;
}

const TableRowPair = forwardRef<HTMLTableRowElement, TableRowPairProps>(function TableRowPair(
  { marketplaceEntity, drivers, index, monetizingCtaLabel, displayAdLabel },
  ref,
) {
  return (
    <>
      <tr ref={ref} data-slot="table-row">
        <TableCellCompany marketplaceEntity={marketplaceEntity} />
        <td className="w-[130px] min-w-[130px] p-0 pt-11 pr-2 align-top md:hidden">
          <NumericLayout
            hideLabel
            iconSize={{ xs: 16, md: 17 }}
            reviewLink={marketplaceEntity.reviewLink ?? undefined}
            starRating={marketplaceEntity.starRating}
          />
          <RiaDates marketplaceEntity={marketplaceEntity} />
        </td>
        <TableDrivers
          drivers={drivers}
          marketplaceEntity={marketplaceEntity as unknown as Record<string, unknown>}
        />
        <td className="hidden w-[160px] p-0 pt-12 align-top lg:table-cell lg:border-b lg:border-border">
          <OfferCta
            displayAdLabel={displayAdLabel}
            index={index}
            marketplaceEntity={marketplaceEntity}
            monetizingCtaLabel={monetizingCtaLabel}
          />
        </td>
      </tr>
      <TableRowLearnMore
        className="hidden md:table-row lg:hidden"
        displayAdLabel={displayAdLabel}
        fullLine
        index={index}
        marketplaceEntity={marketplaceEntity}
        monetizingCtaLabel={monetizingCtaLabel}
      />
      <TableRowLearnMore
        className="md:hidden"
        displayAdLabel={displayAdLabel}
        index={index}
        marketplaceEntity={marketplaceEntity}
        monetizingCtaLabel={monetizingCtaLabel}
      />
    </>
  );
});

function RiaDates({
  marketplaceEntity,
}: {
  marketplaceEntity: FinancialAdvisorsSummaryTableEntityType;
}) {
  if (
    !marketplaceEntity.isRiaOffer ||
    !marketplaceEntity.reviewedAtDate ||
    !marketplaceEntity.periodConsideredDate
  ) {
    return null;
  }

  return (
    <div className="typography-text-small mt-2 flex flex-col text-muted-foreground">
      <span>{marketplaceEntity.reviewedAtDate}</span>
      <span>{marketplaceEntity.periodConsideredDate}</span>
    </div>
  );
}
